// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import type { ActivatePopupDetails } from "./types";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    onClose: () => void;
    details?: ActivatePopupDetails;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * Activation is the one admin action that moves money, so the amount is typed in from the receipt
 * rather than pre-filled. The API compares it against the price and refuses a mismatch; this only
 * catches it a round trip earlier, and never sends a value the admin did not read off the transfer.
 */
export default function ActivatePopup({ onClose, details }: Props) {
    const t = useTranslation();
    const [amount, setAmount] = useState("");

    if (!details) return null;

    const parsed = parseFloat(amount);
    const isMatch = !isNaN(parsed) && parsed === details.expectedAmount;
    const hasMismatch = amount.trim() !== "" && !isMatch;

    const handleConfirm = () => {
        if (!isMatch) return;
        details.onConfirm(parsed);
        onClose();
    };

    return (
        <div className="activate-popup">
            <p className="activate-popup-title">{t.activatePopup.title}</p>
            <p className="activate-popup-store">{details.storeName}</p>

            <div className="activate-popup-expected">
                <span>{t.activatePopup.expected}</span>
                <strong dir="ltr">{details.expectedAmount} EGP</strong>
            </div>

            {details.willQueue && <p className="activate-popup-warning">{t.activatePopup.queuedWarning}</p>}

            <label className="activate-popup-field">
                <span className="activate-popup-label">{t.activatePopup.amountLabel}</span>
                <input
                    className={`activate-popup-input${hasMismatch ? " activate-popup-input--error" : ""}`}
                    type="number"
                    inputMode="decimal"
                    dir="ltr"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    autoFocus
                />
            </label>

            {hasMismatch && <p className="activate-popup-error">{t.activatePopup.mismatch}</p>}

            <div className="activate-popup-actions">
                <button type="button" className="activate-popup-cancel" onClick={onClose}>
                    {t.activatePopup.cancel}
                </button>
                <button type="button" className="activate-popup-confirm" onClick={handleConfirm} disabled={!isMatch}>
                    {t.activatePopup.confirm}
                </button>
            </div>
        </div>
    );
}
